import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, CalendarDays, XCircle, Languages } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function InterpreterAvailability() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [weekly, setWeekly] = useState({});
  const [blocked, setBlocked] = useState([]);
  const [newDate, setNewDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/interpreter-availability/${token}`)
      .then((r) => {
        setData(r.data);
        setWeekly(r.data.availability.weekly || {});
        setBlocked(r.data.availability.blocked_dates || []);
      })
      .catch(() => setError("This link is invalid or has expired. Please contact Soarlife at (407) 374-9834."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(`${API}/interpreter-availability/${token}`, { weekly, blocked_dates: blocked });
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    } catch {
      setError("Could not save — please try again.");
    } finally {
      setSaving(false);
    }
  };

  const addBlock = () => {
    if (newDate && !blocked.includes(newDate)) {
      setBlocked([...blocked, newDate].sort());
      setNewDate("");
    }
  };

  const inputCls =
    "bg-white border border-[#BCB3A6]/50 rounded-2xl px-4 py-2.5 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]";

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="interpreter-availability-page">
      <Header />
      <main className="max-w-2xl mx-auto px-5 py-12">
        <div className="text-center mb-8">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#AE8F78] font-semibold mb-2">
            Soarlife Consulting
          </div>
          <h1 className="font-serif italic text-3xl sm:text-4xl text-[#3F5043] mb-2">
            Your availability
          </h1>
          {data && (
            <p className="text-sm text-[#3F5043]/70">
              {data.full_name} ·{" "}
              {data.languages.map((l) => (l === "ht" ? "Haitian Creole" : "French")).join(" · ")}
            </p>
          )}
        </div>

        {error && (
          <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-6 text-center text-sm text-red-700" data-testid="availability-error">
            {error}
          </div>
        )}
        {!data && !error && (
          <div className="flex justify-center py-12">
            <Loader2 size={26} className="animate-spin text-[#899480]" />
          </div>
        )}

        {data && (
          <div className="space-y-6">
            {/* Weekly availability */}
            <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-6" data-testid="weekly-editor">
              <div className="font-serif text-xl text-[#3F5043] mb-1">Which days are you usually available?</div>
              <p className="text-xs text-[#3F5043]/60 mb-4">
                Soarlife checks this before accepting interpretation requests.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {DAYS.map((d, i) => (
                  <button
                    key={d}
                    type="button"
                    data-testid={`day-${i}`}
                    onClick={() => setWeekly({ ...weekly, [String(i)]: !weekly[String(i)] })}
                    className={`rounded-2xl px-3 py-3 text-sm transition-colors ${
                      weekly[String(i)]
                        ? "bg-[#3F5043] text-[#F8F7F3] font-medium"
                        : "bg-[#F8F7F3] text-[#3F5043]/50 hover:bg-[#899480]/20"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>

            {/* Blocked dates */}
            <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-6" data-testid="blocked-editor">
              <div className="font-serif text-xl text-[#3F5043] mb-1">Days you can't do</div>
              <p className="text-xs text-[#3F5043]/60 mb-4">
                Vacations, personal days — add any specific date you're unavailable.
              </p>
              <div className="flex gap-2 mb-4">
                <input
                  data-testid="block-date-input"
                  type="date"
                  value={newDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setNewDate(e.target.value)}
                  className={inputCls}
                />
                <button
                  onClick={addBlock}
                  data-testid="block-date-add"
                  className="rounded-full px-5 py-2.5 bg-[#875F45] text-[#F8F7F3] text-sm font-medium hover:bg-[#3F5043] transition-colors"
                >
                  Block date
                </button>
              </div>
              {blocked.length === 0 ? (
                <p className="text-sm text-[#3F5043]/50">No blocked dates.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {blocked.map((d) => (
                    <span
                      key={d}
                      data-testid={`blocked-${d}`}
                      className="inline-flex items-center gap-2 bg-[#F8F7F3] border border-[#BCB3A6]/40 rounded-full px-3 py-1.5 text-sm text-[#3F5043]"
                    >
                      {d}
                      <button
                        onClick={() => setBlocked(blocked.filter((x) => x !== d))}
                        data-testid={`unblock-${d}`}
                        className="text-[#875F45] hover:text-red-700"
                      >
                        <XCircle size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Upcoming assignments */}
            {data.assignments.length > 0 && (
              <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-6" data-testid="assignments-list">
                <div className="font-serif text-xl text-[#3F5043] mb-4">Your upcoming assignments</div>
                <div className="space-y-2">
                  {data.assignments.map((a, i) => (
                    <div key={i} className="flex items-center gap-3 bg-[#F8F7F3] rounded-2xl px-4 py-3">
                      <CalendarDays size={15} className="text-[#AE8F78] shrink-0" />
                      <div className="text-sm text-[#3F5043]">
                        {a.appointment_date} at {a.appointment_time} · {a.clinic_name} ·{" "}
                        {a.language === "ht" ? "Haitian Creole" : "French"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={save}
                disabled={saving}
                data-testid="availability-save"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors disabled:opacity-40"
              >
                {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                Save my availability
              </button>
              {saved && (
                <span className="text-sm text-[#899480]" data-testid="availability-saved">Saved — thank you!</span>
              )}
            </div>

            <div className="text-center">
              <Link to="/" className="text-xs text-[#3F5043]/60 hover:text-[#3F5043] inline-flex items-center gap-1">
                <Languages size={12} /> soarlifeconsulting.com
              </Link>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
